<template>
  <section>
    <el-collapse v-model="invoices.filtersOpened">
      <el-collapse-item
        :title="$t('common.search')"
        name="invoices-search-section"
        id="invoices-search-section"
      >
        <el-form
          id="invoices-search-section-form"
          class="search-section-form"
          :model="searchForm"
          ref="searchForm"
          label-position="right"
          size="mini"
          inline
        >
          <el-row :gutter="10">
            <el-col :span="8">
              <el-form-item :label="$t('layout.dateFrom')">
                <el-date-picker
                    class="el-dropdown-new drop-sp date-dob"
                    @change="handleSearch"
                    v-model="searchForm.dateFrom"
                    format="MM/dd/yyyy"
                    :clearable="false"
                    type="date"
                    id="searchForm-dateFrom"
                ></el-date-picker>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('layout.dateTo')">
                <el-date-picker
                    class="el-dropdown-new drop-sp date-dob"
                    @change="handleSearch"
                    v-model="searchForm.dateTo"
                    format="MM/dd/yyyy"
                    clearable
                    type="date"
                    id="searchForm-dateTo"
                ></el-date-picker>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('invoices.invoiceNum')">
                <el-input
                    v-model="searchForm.invoiceNumber"
                    v-alphanumeric-validation
                    maxlength="20"
                    clearable
                    @clear="handleSearch"
                    v-on:keyup.enter.native="handleSearch"
                    class="inline-input"
                    ref="filter-invoiceNum"
                    id="searchForm-invoiceNum" />
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="10">
            <el-col :span="8">
              <el-form-item :label="$t('invoices.billTo')">
                <autocomplete
                    :model.sync="searchForm.billToAccountId"
                    :invoiceNum="null"
                    labelFieldLastWithDash="number"
                    popperAppendToBody
                    :url="urlBillTo"
                    :selectAction="handleSearch"
                    :enterKeyAction="handleSearch"
                    :clearAction="handleSearch"
                    :includeInactive="true"
                    labelField="name"
                    valueField="id"
                    id="searchForm-billTo"
                />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('outbound.units.consignee')">
                <autocomplete
                    :model.sync="searchForm.consigneeAccountId"
                    :invoiceNum="null"
                    labelFieldLastWithDash="number"
                    popperAppendToBody
                    :url="urlConsignee"
                    :selectAction="handleSearch"
                    :enterKeyAction="handleSearch"
                    :clearAction="handleSearch"
                    :includeInactive="true"
                    labelField="name"
                    valueField="id"
                    id="searchForm-consignee"
                />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('invoices.deliverTo')">
                <autocomplete
                    :model.sync="searchForm.deliverToAccountId"
                    labelFieldLastWithDash="number"
                    popperAppendToBody
                    :url="urlConsignee"
                    :selectAction="handleSearch"
                    :enterKeyAction="handleSearch"
                    :clearAction="handleSearch"
                    :includeInactive="true"
                    labelField="name"
                    valueField="id"
                    id="searchForm-deliverTo"
                />
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="10">
            <el-col :span="8">
              <el-form-item :label="$t('invoices.invoiceType')">
                <el-select
                    v-model="searchForm.type"
                    id="searchForm-invoiceType"
                    @change="handleSearch"
                >
                  <el-option
                      v-for="option in invoiceStatusOptions"
                      :key="option.value"
                      :label="option.label"
                      :value="option.value"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('extensionRequest.state')">
                <state-select v-model="searchForm.billToStateId" @change="handleSearch" :includeOptionAll="true" />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('extensionRequest.city')">
                <el-input
                    v-model="searchForm.billToCity"
                    v-alphanumeric-validation
                    maxlength="100"
                    clearable
                    @clear="handleSearch"
                    v-on:keyup.enter.native="handleSearch"
                    class="inline-input"
                    ref="filter-billToCity"
                    id="searchForm-billToCity" />
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="10">
            <el-col :span="8">
              <el-form-item :label="$t('common.features')">
                <el-select
                  v-model="featuresSelected"
                  multiple
                  collapse-tags
                  @change="handleSearch"
                >
                  <el-option
                    v-for="item in features"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('invoices.deleted')">
                <el-select v-model="searchForm.active" @change="handleSearch">
                  <el-option
                      v-for="item in deletedOptions"
                      :key="item.value"
                      :label="item.label"
                      :value="item.value"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('invoices.posted')">
                <el-select v-model="searchForm.posted" @change="handleSearch">
                  <el-option
                      v-for="item in allYesNoOptions"
                      :key="item.value"
                      :label="item.label"
                      :value="item.value"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="10">
            <el-col :span="8">
              <el-form-item :label="$t('invoices.credited')">
                <el-select v-model="searchForm.credited" @change="handleSearch">
                  <el-option
                      v-for="item in allYesNoOptions"
                      :key="item.value"
                      :label="item.label"
                      :value="item.value"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item :label="$t('customerService.shipment.loadNumber')">
                <el-input
                  v-model="searchForm.loadNumber"
                  v-alphanumeric-validation
                  maxlength="20"
                  clearable
                  @clear="handleSearch"
                  v-on:keyup.enter.native="handleSearch"
                  class="inline-input"
                  ref="filter-loadNumber"
                  id="searchForm-loadNumber"
                ></el-input>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="10">
            <el-col :span="24" class="text-align-end">
              <el-tooltip
                :content="$t('common.search')"
                class="item"
                effect="dark"
                placement="top-start"
              >
                <el-button
                  id="invoices-search-section-form-button"
                  class="button-action-default"
                  @click="handleSearch"
                  icon="el-icon-search"
                  size="mini"
                ></el-button>
              </el-tooltip>
            </el-col>
          </el-row>
        </el-form>
      </el-collapse-item>
    </el-collapse>
  </section>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import Autocomplete from '@/components/Autocomplete';
import constants from '@/utils/constants';
import apiConstants from '@/utils/apiConstants';
import StateSelect from '@/views/routing/components/StateSelect';
import {
  INVOICES_SEARCH,
  INVOICES_SET_IS_IN_BULK_PRINTING,
  INVOICES_SET_IS_IN_UPLOAD_TO_VERSAPAY
} from '@/store/actions';

export default {
  name: 'InvoicesSearchSection',
  components: { Autocomplete, StateSelect },
  computed: {
    ...mapGetters(['invoices']),
    deletedOptions() {
      return [
        {
          value: constants.STATUS_BOOLEAN.yes.value,
          label: constants.STATUS_BOOLEAN.no.label
        },
        {
          value: constants.STATUS_BOOLEAN.no.value,
          label: constants.STATUS_BOOLEAN.yes.label
        }
      ];
    },
    allYesNoOptions() {
      return [
        {
          value: null,
          label: 'All'
        },
        {
          value: constants.STATUS_BOOLEAN.yes.value,
          label: constants.STATUS_BOOLEAN.yes.label
        },
        {
          value: constants.STATUS_BOOLEAN.no.value,
          label: constants.STATUS_BOOLEAN.no.label
        }
      ];
    },
    features() {
      return constants.FILTER_FEATURES;
    }
  },
  data() {
    return {
      activeCollapsibleName: ['invoices-search-section'],
      isOpen: ['invoices-search-section'],
      searchForm: {
        active: constants.STATUS_BOOLEAN.yes.value,
        dateFrom: new Date(),
        dateTo: null,
        billToAccountId: null,
        consigneeAccountId: null,
        deliverToAccountId: null,
        invoiceNumber: null,
        billToStateId: null,
        billToCity: null,
        type: null,
        posted: true,
        credited: null,
        loadNumber: null,
        page: constants.TABLES.DEFAULT_PAGE
      },
      featuresSelected: [],
      invoiceStatusOptions: constants.INVOICE.TYPES,
      urlBillTo: apiConstants.END_POINTS.ACCOUNTS.BILL_TO,
      urlConsignee: apiConstants.END_POINTS.ACCOUNTS.CONSIGNEE,
      urlStates: apiConstants.END_POINTS.ROUTING.STATES,
    };
  },
  watch: {
    'searchForm.dateFrom': function updateFrom(newVal) {
      const newDayOfServiceFrom = moment(newVal);
      if (newDayOfServiceFrom.isAfter(moment(this.searchForm.dateTo))) {
        this.searchForm.dateTo = newVal;
      }
    },
    'searchForm.dateTo': function updateTo(newVal) {
      const newDayOfServiceTo = moment(newVal);
      if (newDayOfServiceTo.isBefore(moment(this.searchForm.dateFrom))) {
        this.searchForm.dateFrom = newVal;
      }
    }
  },
  methods: {
    ...mapActions({
      search: INVOICES_SEARCH,
      setIsInBulkPrinting: INVOICES_SET_IS_IN_BULK_PRINTING,
      setIsInUploadToVersapay: INVOICES_SET_IS_IN_UPLOAD_TO_VERSAPAY
    }),
    handleSearch() {
      const featureFields = {};
      for (const feature of this.features) {
        featureFields[feature.value] = this.featuresSelected.some(featureSelected => feature.value === featureSelected);
      }

      this.search({ ...this.searchForm, ...featureFields });
      this.setIsInBulkPrinting(false);
      this.setIsInUploadToVersapay(false);
    }
  },
  mounted() {
    this.handleSearch();
  }
};
</script>
